/*
 * OAuth im Taskpane: Anmeldung und Consent direkt im Taskpane statt in einem separaten Dialog.
 * Verwendet Authorization Code Flow mit PKCE (kein Client-Secret nötig).
 */

import { API_BASE_URL, AUTH_CLIENT_ID } from "../config";

const STORAGE_KEY_TOKEN = "sunnysign_access_token";
const STORAGE_KEY_EXPIRES = "sunnysign_token_expires";
const STORAGE_KEY_CODE_VERIFIER = "sunnysign_code_verifier";

/** Tokens gelten lokal schon etwas vor dem echten Ablauf als abgelaufen (Clock-Skew / Race). */
const TOKEN_EXPIRY_SKEW_MS = 2 * 60 * 1000;
/** Fallback, wenn weder expires_in noch JWT-exp vorliegt (Office-SSO oft ~1 h). */
const DEFAULT_TOKEN_LIFETIME_SEC = 50 * 60;

// ─── code_verifier Hilfsfunktionen ──────────────────────────────────────────
// Manche Office/Outlook WebViews (besonders neue Versionen, Mobile, WKWebView)
// löschen sessionStorage beim OAuth-Redirect zu login.microsoftonline.com und zurück.
// Lösung: code_verifier parallel in localStorage sichern (kurzlebig, wird nach Verwendung gelöscht).
function saveCodeVerifier(value: string): void {
  try { sessionStorage.setItem(STORAGE_KEY_CODE_VERIFIER, value); } catch { /* ignore */ }
  try { localStorage.setItem(STORAGE_KEY_CODE_VERIFIER, value); } catch { /* ignore */ }
  // Ablaufzeit: 10 Minuten (schützt vor alten Einträgen)
  try { localStorage.setItem(STORAGE_KEY_CODE_VERIFIER + "_exp", String(Date.now() + 10 * 60 * 1000)); } catch { /* ignore */ }
}

function loadCodeVerifier(): string | null {
  // Zuerst sessionStorage, dann localStorage als Fallback
  try {
    const s = sessionStorage.getItem(STORAGE_KEY_CODE_VERIFIER);
    if (s) return s;
  } catch { /* ignore */ }
  try {
    const exp = localStorage.getItem(STORAGE_KEY_CODE_VERIFIER + "_exp");
    if (exp && parseInt(exp, 10) < Date.now()) {
      // Abgelaufen – aufräumen
      localStorage.removeItem(STORAGE_KEY_CODE_VERIFIER);
      localStorage.removeItem(STORAGE_KEY_CODE_VERIFIER + "_exp");
      return null;
    }
    return localStorage.getItem(STORAGE_KEY_CODE_VERIFIER);
  } catch { /* ignore */ }
  return null;
}

function deleteCodeVerifier(): void {
  try { sessionStorage.removeItem(STORAGE_KEY_CODE_VERIFIER); } catch { /* ignore */ }
  try { localStorage.removeItem(STORAGE_KEY_CODE_VERIFIER); } catch { /* ignore */ }
  try { localStorage.removeItem(STORAGE_KEY_CODE_VERIFIER + "_exp"); } catch { /* ignore */ }
}
/** RoamingSettings-Key: Consent wurde pro Tenant bereits erteilt – bei Token-Ablauf nur Login, kein erneuter Consent. */
const ROAMING_KEY_CONSENT_GIVEN = "sunnysign_consent_given";

const AUTHORITY = "https://login.microsoftonline.com/common";

/**
 * Name des delegierten API-Scopes unter „App-Registrierung → API veröffentlichen“ (Standard: access_as_user).
 * Ohne dieses Suffix liefert AAD oft AADSTS650053 („Scope existiert nicht auf der Ressource“).
 */
const OAUTH_DELEGATED_API_SCOPE = "access_as_user";

/**
 * Scopes für den Taskpane-OAuth:
 * - api://host/clientId/access_as_user: delegierter Zugriff auf die eigene API (Audience für Backend + OBO).
 * - openid, profile, User.Read: Graph User.Read für OBO (AADSTS65001 vermeiden).
 * Der erste Scope muss in Azure unter „API veröffentlichen“ angelegt und im Manifest (WebApplicationInfo) abgebildet sein.
 */
function getOAuthScopes(): string {
  const host = typeof window !== "undefined" ? window.location.host : "localhost:3000";
  const apiScope = `api://${host}/${AUTH_CLIENT_ID}/${OAUTH_DELEGATED_API_SCOPE}`;
  return `${apiScope} openid profile User.Read`;
}

declare const Office: {
  context?: { roamingSettings?: { get: (key: string) => unknown; set: (key: string, value: string) => void; saveAsync: (callback: (result: unknown) => void) => void } };
  AsyncResultStatus: { Succeeded: string };
};

declare const OfficeRuntime:
  | {
      storage?: {
        getItem: (key: string) => Promise<string | null>;
        setItem: (key: string, value: string) => Promise<void>;
        removeItem: (key: string) => Promise<void>;
      };
    }
  | undefined;

/**
 * OfficeRuntime.storage ist der einzige Token-Speicher, den auch die Classic-Launch-Runtime
 * (autorunweb.js, ohne window/localStorage) lesen kann. Das Taskpane spiegelt Token + Ablauf
 * dorthin, damit OnNewMessageCompose weiterarbeiten kann, wenn OfficeRuntime-SSO ausfällt
 * (z. B. wenn NAA die Origin ablehnt: „not listed in allowed origin“ + SSO 5001).
 */
function getOfficeRuntimeStorage(): NonNullable<typeof OfficeRuntime>["storage"] | null {
  try {
    if (typeof OfficeRuntime === "undefined") return null;
    return OfficeRuntime?.storage ?? null;
  } catch {
    return null;
  }
}

function mirrorTokenToOfficeRuntimeStorage(token: string, expiresAtMs: string): void {
  const storage = getOfficeRuntimeStorage();
  if (!storage) return;
  void storage.setItem(STORAGE_KEY_TOKEN, token).catch(() => undefined);
  void storage.setItem(STORAGE_KEY_EXPIRES, expiresAtMs).catch(() => undefined);
}

export function clearOfficeRuntimeStorageToken(): void {
  const storage = getOfficeRuntimeStorage();
  if (!storage) return;
  void storage.removeItem(STORAGE_KEY_TOKEN).catch(() => undefined);
  void storage.removeItem(STORAGE_KEY_EXPIRES).catch(() => undefined);
}

/** Async-Pendant zu getStoredToken() für Kontexte ohne window (Classic-Launch-Runtime). */
export async function getStoredTokenFromOfficeRuntime(): Promise<string | null> {
  const storage = getOfficeRuntimeStorage();
  if (!storage) return null;
  try {
    const token = await storage.getItem(STORAGE_KEY_TOKEN);
    if (!token) return null;
    let expiresMs = NaN;
    try {
      const raw = await storage.getItem(STORAGE_KEY_EXPIRES);
      if (raw) expiresMs = parseInt(raw, 10);
    } catch {
      /* ignore */
    }
    if (isNaN(expiresMs)) {
      const expUnix = parseJwtExpUnixSeconds(token);
      if (expUnix != null) expiresMs = expUnix * 1000;
    }
    if (!isNaN(expiresMs) && expiresMs - TOKEN_EXPIRY_SKEW_MS < Date.now()) {
      clearOfficeRuntimeStorageToken();
      return null;
    }
    return token;
  } catch {
    return null;
  }
}

/**
 * Redirect-URI für OAuth: Taskpane-URL (gleiche Origin wie das Add-in).
 * Muss in der Azure App-Registrierung unter "Authentifizierung" → "SPA" eingetragen sein.
 */
export function getRedirectUri(): string {
  const base = typeof window !== "undefined" ? window.location.origin : "";
  return `${base}/taskpane.html`;
}

export function getFallbackAuthRedirectUri(): string {
  const base = typeof window !== "undefined" ? window.location.origin : "";
  return `${base}/fallbackauthdialog.html`;
}

export function isLaunchAuthDialogModeFromUrl(): boolean {
  if (typeof window === "undefined") return false;
  return new URLSearchParams(window.location.search).get("sunnysign_launch_auth") === "1";
}

export function isLaunchAuthConsentRequiredFromUrl(): boolean {
  if (typeof window === "undefined") return true;
  return new URLSearchParams(window.location.search).get("consent") === "1";
}

export function getLaunchAuthOAuthBridgeKeyFromUrl(): string | null {
  try {
    const k = new URLSearchParams(window.location.search).get("oauth_bridge") || "";
    return /^[a-f0-9]{32}$/i.test(k) ? k.toLowerCase() : null;
  } catch {
    return null;
  }
}

/** displayDialogAsync-Kind: messageParent verfügbar — immer Launch-Auth, nie volle Taskpane-UI. */
export function isOfficeDialogChild(): boolean {
  try {
    if (typeof Office === "undefined") return false;
    return typeof (Office.context as { ui?: { messageParent?: unknown } } | undefined)?.ui?.messageParent === "function";
  } catch {
    return false;
  }
}

/** Start-URL für displayDialogAsync: taskpane.html im Launch-Modus (Redirect-URI bleibt taskpane.html wie Prod). */
export function getAuthDialogStartUrl(requireConsent: boolean, oauthBridgeKey?: string): string {
  const base = getRedirectUri();
  const sep = base.indexOf("?") >= 0 ? "&" : "?";
  let url = `${base}${sep}sunnysign_launch_auth=1&consent=${requireConsent ? "1" : "0"}`;
  if (oauthBridgeKey && /^[a-f0-9]{32}$/i.test(oauthBridgeKey)) {
    url += `&oauth_bridge=${oauthBridgeKey.toLowerCase()}`;
  }
  return url;
}

/**
 * Prüft, ob die aktuelle URL ein OAuth-Callback ist (code oder Fehler in Query/Fragment).
 */
export function isAuthCallback(): boolean {
  if (typeof window === "undefined") return false;
  const params = new URLSearchParams(window.location.search);
  const hash = new URLSearchParams(window.location.hash.slice(1));
  return params.has("code") || params.has("error") || hash.has("access_token") || hash.has("error");
}

/**
 * Liest den gespeicherten Access Token (falls vorhanden und noch nicht abgelaufen).
 * Zuerst sessionStorage (gleicher Kontext wie Taskpane), dann localStorage: Autorun (autorunweb.html)
 * und Taskpane laufen in getrennten Office-iframes – sessionStorage ist nicht geteilt, localStorage
 * gleicher Origin schon. So kann OnNewMessageCompose dasselbe OAuth-Token nutzen wie das Taskpane.
 */
export function getStoredToken(): string | null {
  if (typeof window === "undefined") return null;
  const tryStore = (store: Storage): string | null => {
    try {
      const token = store.getItem(STORAGE_KEY_TOKEN);
      if (!token) return null;
      let expiresMs = NaN;
      const expiresRaw = store.getItem(STORAGE_KEY_EXPIRES);
      if (expiresRaw) expiresMs = parseInt(expiresRaw, 10);
      // Ältere Einträge ohne Expiry: Ablauf aus JWT ableiten, sonst 401-Retry.
      if (isNaN(expiresMs)) {
        const expUnix = parseJwtExpUnixSeconds(token);
        if (expUnix != null) expiresMs = expUnix * 1000;
      }
      if (!isNaN(expiresMs) && expiresMs - TOKEN_EXPIRY_SKEW_MS < Date.now()) {
        return "__expired__";
      }
      return token;
    } catch {
      return null;
    }
  };
  let t = tryStore(sessionStorage);
  if (t === "__expired__") {
    clearStoredToken();
    return null;
  }
  if (t) return t;
  t = tryStore(localStorage);
  if (t === "__expired__") {
    clearStoredToken();
    return null;
  }
  return t;
}

/**
 * Kopiert ein nur in sessionStorage liegendes Token in localStorage und OfficeRuntime.storage
 * (z. B. nach Update), damit Autorun/OnNewMessageCompose dasselbe Token lesen kann.
 */
export function mirrorTokenToLocalStorageIfPresent(): void {
  if (typeof window === "undefined") return;
  try {
    const t = sessionStorage.getItem(STORAGE_KEY_TOKEN);
    const exp = sessionStorage.getItem(STORAGE_KEY_EXPIRES);
    if (!t) return;
    if (exp && parseInt(exp, 10) < Date.now()) return;
    localStorage.setItem(STORAGE_KEY_TOKEN, t);
    if (exp) localStorage.setItem(STORAGE_KEY_EXPIRES, exp);
    mirrorTokenToOfficeRuntimeStorage(t, exp ?? String(Date.now() + resolveExpiresInSeconds(t) * 1000));
  } catch {
    /* ignore */
  }
}

/**
 * Speichert den Access Token und immer eine Ablaufzeit
 * (explizites expires_in → JWT exp → konservativer Default).
 */
export function setStoredToken(token: string, expiresIn?: number): void {
  const lifetimeSec = resolveExpiresInSeconds(token, expiresIn);
  const exp = String(Date.now() + lifetimeSec * 1000);
  mirrorTokenToOfficeRuntimeStorage(token, exp);
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(STORAGE_KEY_TOKEN, token);
  } catch {
    /* ignore */
  }
  try {
    localStorage.setItem(STORAGE_KEY_TOKEN, token);
  } catch {
    /* ignore */
  }
  try {
    sessionStorage.setItem(STORAGE_KEY_EXPIRES, exp);
  } catch {
    /* ignore */
  }
  try {
    localStorage.setItem(STORAGE_KEY_EXPIRES, exp);
  } catch {
    /* ignore */
  }
}

/** JWT-Claim `exp` (Unix-Sekunden), ohne Signaturprüfung — nur für lokale Ablaufanzeige. */
export function parseJwtExpUnixSeconds(token: string): number | null {
  try {
    const parts = token.split(".");
    if (parts.length < 2) return null;
    const b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const pad = b64.length % 4 === 0 ? "" : "=".repeat(4 - (b64.length % 4));
    const json = atob(b64 + pad);
    const claims = JSON.parse(json) as { exp?: unknown };
    return typeof claims.exp === "number" && claims.exp > 0 ? claims.exp : null;
  } catch {
    return null;
  }
}

/** Sekunden bis Ablauf: explizit → JWT → Default. Nie negativ. */
export function resolveExpiresInSeconds(token: string, expiresIn?: number): number {
  if (typeof expiresIn === "number" && !isNaN(expiresIn) && expiresIn > 0) {
    return Math.floor(expiresIn);
  }
  const expUnix = parseJwtExpUnixSeconds(token);
  if (expUnix != null) {
    const sec = expUnix - Math.floor(Date.now() / 1000);
    return sec > 0 ? sec : 0;
  }
  return DEFAULT_TOKEN_LIFETIME_SEC;
}

/**
 * Entfernt gespeicherten Token und Code-Verifier.
 */
export function clearStoredToken(): void {
  clearOfficeRuntimeStorageToken();
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(STORAGE_KEY_TOKEN);
  } catch {
    /* ignore */
  }
  try {
    sessionStorage.removeItem(STORAGE_KEY_EXPIRES);
  } catch {
    /* ignore */
  }
  try {
    localStorage.removeItem(STORAGE_KEY_TOKEN);
  } catch {
    /* ignore */
  }
  try {
    localStorage.removeItem(STORAGE_KEY_EXPIRES);
  } catch {
    /* ignore */
  }
  deleteCodeVerifier();
}

/**
 * Prüft, ob für den aktuellen Tenant bereits Consent erteilt wurde (RoamingSettings, pro Tenant).
 * Bei true: bei erneutem Login nur Anmeldung (prompt=login), kein erneuter Consent.
 */
export function getConsentGiven(): boolean {
  if (typeof Office === "undefined" || !Office.context?.roamingSettings) return false;
  return Office.context.roamingSettings.get(ROAMING_KEY_CONSENT_GIVEN) === "true";
}

/**
 * Speichert, dass Consent für diesen Tenant erteilt wurde (einmalig pro Tenant).
 */
/** Gibt true zurueck wenn der Code in einem displayDialogAsync-Dialog laeuft (messageParent verfuegbar). */
function isInDialogContext(): boolean {
  return isOfficeDialogChild();
}

export function setConsentGiven(): void {
  if (typeof Office === "undefined" || !Office.context?.roamingSettings) return;
  try {
    Office.context.roamingSettings.set(ROAMING_KEY_CONSENT_GIVEN, 'true');
    if (isInDialogContext()) return; // saveAsync throws async in dialog contexts
    Office.context.roamingSettings.saveAsync((r: any) => {
      if (r && r.status !== Office.AsyncResultStatus.Succeeded) {
        console.warn('[SunnySign] roamingSettings.saveAsync failed:', r.error);
      }
    });
  } catch { /* ignore */ }
}

/** Nach „anderes Konto“: gespeicherte Zustimmung zurücksetzen (neuer Nutzer braucht ggf. neuen Consent). */
export function clearConsentGiven(): void {
  if (typeof Office === "undefined" || !Office.context?.roamingSettings) return;
  try {
    Office.context.roamingSettings.set(ROAMING_KEY_CONSENT_GIVEN, '');
    if (isInDialogContext()) return;
    Office.context.roamingSettings.saveAsync((r: any) => {
      if (r && r.status !== Office.AsyncResultStatus.Succeeded) {
        console.warn('[SunnySign] roamingSettings.saveAsync (clear) failed:', r.error);
      }
    });
  } catch { /* ignore */ }
}

/**
 * Gibt den zu verwendenden Access Token zurück: zuerst aus Taskpane-OAuth, sonst null.
 * Das Taskpane zeigt dann die Consent-Ansicht und startet den Login im Taskpane.
 */
export function getAccessToken(): string | null {
  return getStoredToken();
}

/**
 * Generiert einen zufälligen PKCE code_verifier (base64url).
 */
function generateCodeVerifier(): string {
  const array = new Uint8Array(32);
  if (typeof crypto !== "undefined" && crypto.getRandomValues) {
    crypto.getRandomValues(array);
  }
  return base64UrlEncode(array);
}

/**
 * Berechnet den PKCE code_challenge aus dem code_verifier (S256).
 */
async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const hash = await crypto.subtle.digest("SHA-256", data);
  return base64UrlEncode(new Uint8Array(hash));
}

function base64UrlEncode(buffer: Uint8Array): string {
  const base64 = btoa(String.fromCharCode.apply(null, Array.from(buffer)));
  return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/** Legacy: Popup ohne Server-Brücke (nur noch Fallback). */
const OAUTH_POPUP_LEGACY_PREFIX = "SSPO1_";
const OAUTH_POPUP_STATE_MARKER = "SSPO1";
/** Mobil-OAuth-Dialog (displayDialogAsync): State überlebt iOS-WKWebView-Redirect ohne localStorage. */
const OAUTH_LAUNCH_DIALOG_STATE_MARKER = "SSLAUNCH";

export function isOAuthLaunchDialogState(state: string): boolean {
  return !!state && state.startsWith(`${OAUTH_LAUNCH_DIALOG_STATE_MARKER}.`);
}

/** 32 Hex-Zeichen (128 Bit) für die API-Brücke Safari OWA ↔ Taskpane (kein gemeinsamer localStorage). */
export function generateOAuthBridgeKey(): string {
  const a = new Uint8Array(16);
  if (typeof crypto !== "undefined" && crypto.getRandomValues) {
    crypto.getRandomValues(a);
  }
  return Array.from(a, (b) => b.toString(16).padStart(2, "0")).join("");
}

export function parseOAuthPopupMetaFromState(state: string): {
  wasPopupOAuth: boolean;
  bridgeKey: string | null;
  wasLaunchDialogOAuth?: boolean;
} {
  if (!state) return { wasPopupOAuth: false, bridgeKey: null };
  if (state.startsWith(`${OAUTH_LAUNCH_DIALOG_STATE_MARKER}.`)) {
    const parts = state.split(".");
    if (parts.length >= 3 && parts[0] === OAUTH_LAUNCH_DIALOG_STATE_MARKER && /^[a-f0-9]{32}$/i.test(parts[1])) {
      return { wasPopupOAuth: false, bridgeKey: parts[1].toLowerCase(), wasLaunchDialogOAuth: true };
    }
    return { wasPopupOAuth: false, bridgeKey: null, wasLaunchDialogOAuth: true };
  }
  if (state.startsWith(`${OAUTH_POPUP_STATE_MARKER}.`)) {
    const parts = state.split(".");
    if (parts.length >= 3 && parts[0] === OAUTH_POPUP_STATE_MARKER && /^[a-f0-9]{32}$/i.test(parts[1])) {
      return { wasPopupOAuth: true, bridgeKey: parts[1].toLowerCase() };
    }
    return { wasPopupOAuth: true, bridgeKey: null };
  }
  if (state.startsWith(OAUTH_POPUP_LEGACY_PREFIX)) return { wasPopupOAuth: true, bridgeKey: null };
  return { wasPopupOAuth: false, bridgeKey: null };
}

/**
 * Token an die SunnySign-API-Brücke senden (Safari OWA: Taskpane pollt GET).
 */
export async function notifyOAuthBridgeFromPopup(
  bridgeKey: string,
  accessToken: string,
  expiresIn?: number
): Promise<void> {
  const key = encodeURIComponent(bridgeKey.toLowerCase());
  const res = await fetch(`${API_BASE_URL}/api/auth-bridge/${key}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ accessToken, expiresIn }),
    credentials: "omit",
    cache: "no-store",
  });
  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    throw new Error(`auth-bridge POST ${res.status}: ${txt.slice(0, 200)}`);
  }
}

/** Taskpane: PKCE-Verifier serverseitig ablegen (Popup hat nach AAD-Redirect keinen gemeinsamen Storage). */
async function registerOAuthBridgePkceVerifier(bridgeKey: string, codeVerifier: string): Promise<void> {
  const k = encodeURIComponent(bridgeKey.toLowerCase());
  const res = await fetch(`${API_BASE_URL}/api/auth-bridge/${k}/pkce-verifier`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ codeVerifier }),
    credentials: "omit",
    cache: "no-store",
  });
  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    throw new Error(`auth-bridge pkce-verifier ${res.status}: ${txt.slice(0, 200)}`);
  }
}

async function registerBridgePkceIfNeeded(oauthBridgeKey: string, codeVerifier: string): Promise<void> {
  if (!/^[a-f0-9]{32}$/i.test(oauthBridgeKey)) return;
  await registerOAuthBridgePkceVerifier(oauthBridgeKey, codeVerifier);
}

/**
 * Popup: PKCE-Verifier einmalig von der API (Taskpane hat ihn registriert).
 * Der eigentliche Code-Tausch muss im Browser gegen AAD laufen (SPA / AADSTS9002327).
 */
async function consumePkceVerifierFromBridge(bridgeKey: string): Promise<string | null> {
  const k = encodeURIComponent(bridgeKey.toLowerCase());
  const res = await fetch(`${API_BASE_URL}/api/auth-bridge/${k}/consume-pkce-verifier`, {
    method: "POST",
    headers: { Accept: "application/json" },
    credentials: "omit",
    cache: "no-store",
  });
  if (!res.ok) return null;
  const data = (await res.json()) as { codeVerifier?: string | null };
  if (typeof data.codeVerifier !== "string" || !data.codeVerifier) return null;
  return data.codeVerifier;
}

/**
 * Baut die Microsoft-Anmelde-URL für den Authorization-Code-Flow mit PKCE.
 * requireConsent: true = erstes Mal / Consent anzeigen; false = nur Login (Token abgelaufen, Consent bereits erteilt).
 */
export async function buildLoginUrl(
  requireConsent: boolean,
  options?: { popupFlow?: boolean; launchDialogFlow?: boolean; oauthBridgeKey?: string }
): Promise<{ url: string; codeVerifier: string }> {
  const codeVerifier = generateCodeVerifier();
  const codeChallenge = await generateCodeChallenge(codeVerifier);
  let state: string;
  if (options?.launchDialogFlow) {
    const nonce = generateCodeVerifier().slice(0, 16);
    if (options.oauthBridgeKey && /^[a-f0-9]{32}$/i.test(options.oauthBridgeKey)) {
      state = `${OAUTH_LAUNCH_DIALOG_STATE_MARKER}.${options.oauthBridgeKey.toLowerCase()}.${nonce}`;
    } else {
      state = `${OAUTH_LAUNCH_DIALOG_STATE_MARKER}.${nonce}`;
    }
  } else if (options?.popupFlow && options.oauthBridgeKey && /^[a-f0-9]{32}$/i.test(options.oauthBridgeKey)) {
    const nonce = generateCodeVerifier().slice(0, 16);
    state = `${OAUTH_POPUP_STATE_MARKER}.${options.oauthBridgeKey.toLowerCase()}.${nonce}`;
  } else if (options?.popupFlow) {
    state = `${OAUTH_POPUP_LEGACY_PREFIX}${generateCodeVerifier().slice(0, 24)}`;
  } else {
    state = generateCodeVerifier().slice(0, 16);
  }

  saveCodeVerifier(codeVerifier);
  try { sessionStorage.setItem("sunnysign_oauth_state", state); } catch { /* ignore */ }
  try { localStorage.setItem("sunnysign_oauth_state", state); } catch { /* ignore */ }

  const params = new URLSearchParams({
    client_id: AUTH_CLIENT_ID,
    response_type: "code",
    redirect_uri: getRedirectUri(),
    response_mode: "query",
    scope: getOAuthScopes(),
    state,
    code_challenge: codeChallenge,
    code_challenge_method: "S256",
    prompt: requireConsent ? "consent" : "login",
  });

  const url = `${AUTHORITY}/oauth2/v2.0/authorize?${params.toString()}`;
  return { url, codeVerifier };
}

/**
 * Startet die Anmeldung nur in diesem Taskpane-WebView (kein displayDialogAsync / kein window.open).
 * Hinweis: Manche Hosts leiten externe OAuth-URLs in den Systembrowser um – dann hilft zuerst Office-SSO im Taskpane.
 */
export async function startLoginInTaskpaneRedirect(
  requireConsent: boolean,
  options?: { launchDialogFlow?: boolean; oauthBridgeKey?: string }
): Promise<void> {
  const { url, codeVerifier } = await buildLoginUrl(requireConsent, options);
  if (options?.launchDialogFlow && options.oauthBridgeKey) {
    await registerBridgePkceIfNeeded(options.oauthBridgeKey, codeVerifier);
  }
  window.location.assign(url);
}

/** Einmaliges Polling der OAuth-API-Brücke (Mobil-Autorun ↔ OAuth-Dialog). */
export async function fetchOAuthBridgeTokenOnce(
  bridgeKey: string
): Promise<{ access_token: string; expires_in?: number } | null> {
  if (!/^[a-f0-9]{32}$/i.test(bridgeKey)) return null;
  try {
    const r = await fetch(
      `${API_BASE_URL}/api/auth-bridge/${encodeURIComponent(bridgeKey.toLowerCase())}?_=${Date.now()}`,
      {
        method: "GET",
        credentials: "omit",
        cache: "no-store",
        headers: { Accept: "application/json", "Cache-Control": "no-cache", Pragma: "no-cache" },
      }
    );
    if (r.status !== 200) return null;
    const j = (await r.json()) as Record<string, unknown>;
    const token =
      (typeof j.access_token === "string" && j.access_token) ||
      (typeof j.accessToken === "string" && j.accessToken) ||
      "";
    if (!token) return null;
    const expRaw = j.expires_in ?? j.expiresIn;
    let expiresIn: number | undefined;
    if (typeof expRaw === "number" && Number.isFinite(expRaw)) expiresIn = expRaw;
    else if (typeof expRaw === "string") {
      const n = parseInt(expRaw, 10);
      if (Number.isFinite(n)) expiresIn = n;
    }
    return { access_token: token, expires_in: expiresIn };
  } catch {
    return null;
  }
}

/** Navigiert ein per window.open('about:blank') geöffnetes Popup zur OAuth-URL (sinnvoll wenn iframe-Redirect von AAD blockiert wird). */
export async function navigatePreparedPopupToOAuth(
  popup: Window,
  requireConsent: boolean,
  oauthBridgeKey: string
): Promise<void> {
  const { url, codeVerifier } = await buildLoginUrl(requireConsent, { popupFlow: true, oauthBridgeKey });
  await registerBridgePkceIfNeeded(oauthBridgeKey, codeVerifier);
  popup.location.href = url;
}

/** Message-Typ für Token-Übergabe vom Login-Popup an den Taskpane-Opener. */
export const AUTH_POPUP_MESSAGE_TYPE = "sunnysign_auth";

/**
 * Startet die Anmeldung in einem Popup-Fenster (empfohlen, wenn Taskpane-Redirect blockiert wird).
 * Nach Consent leitet Microsoft im Popup auf taskpane.html?code=... zurück;
 * das Popup sendet den Token per postMessage an den Opener und schließt sich.
 * requireConsent: true = Login + Consent, false = nur Login.
 */
export async function startLoginInPopup(requireConsent: boolean, oauthBridgeKey: string): Promise<void> {
  const { url, codeVerifier } = await buildLoginUrl(requireConsent, { popupFlow: true, oauthBridgeKey });
  await registerBridgePkceIfNeeded(oauthBridgeKey, codeVerifier);
  const w = window.open("about:blank", "sunnysign_consent", "width=500,height=600,scrollbars=yes,resizable=yes");
  if (!w) return;
  w.location.href = url;
}

export type AuthCallbackResult =
  | {
      success: true;
      access_token: string;
      expires_in?: number;
      wasPopupOAuth?: boolean;
      oauthBridgeKey?: string | null;
      wasLaunchDialogOAuth?: boolean;
    }
  | { success: false; wasLaunchDialogOAuth?: boolean };

/**
 * Verarbeitet den OAuth-Callback (code oder error in URL).
 * Gibt bei Erfolg Token-Daten zurück (für Popup → Opener postMessage), sonst { success: false }.
 */
export async function handleAuthCallback(): Promise<AuthCallbackResult> {
  if (typeof window === "undefined") return { success: false };

  const params = new URLSearchParams(window.location.search);
  const error = params.get("error") || new URLSearchParams(window.location.hash.slice(1)).get("error");
  const stateParam = params.get("state") || "";
  const popupMeta = parseOAuthPopupMetaFromState(stateParam);
  const wasLaunchDialogOAuth = popupMeta.wasLaunchDialogOAuth === true;

  if (error) {
    const desc = params.get("error_description") || "";
    console.error("OAuth error:", error, desc);
    clearStoredToken();
    clearAuthParamsFromUrl();
    return { success: false, wasLaunchDialogOAuth };
  }

  const code = params.get("code");
  if (code) {

    /*
     * Brücken-Popup: Verifier auf dem Server (Taskpane), Token-Tausch im Browser (SPA-Regel bei AAD).
     * Sonst: lokaler PKCE-Speicher (Dialog / gleicher Kontext wie buildLoginUrl).
     */
    let codeVerifier: string | null = null;
    if (popupMeta.bridgeKey) {
      codeVerifier = await consumePkceVerifierFromBridge(popupMeta.bridgeKey);
    }
    if (!codeVerifier) {
      codeVerifier = loadCodeVerifier();
    }
    if (!codeVerifier) {
      console.error("Kein code_verifier: weder API-Brücke noch lokaler Speicher.");
      clearAuthParamsFromUrl();
      return { success: false, wasLaunchDialogOAuth };
    }

    const token = await exchangeCodeForToken(code, codeVerifier, getRedirectUri());
    if (token) {
      setStoredToken(token.access_token, token.expires_in);
      setConsentGiven();
      if (popupMeta.bridgeKey) {
        try {
          await notifyOAuthBridgeFromPopup(popupMeta.bridgeKey, token.access_token, token.expires_in);
        } catch (e) {
          console.warn("[SunnySign] OAuth-Brücke: POST fehlgeschlagen", e);
        }
      }
      deleteCodeVerifier();
      try { sessionStorage.removeItem("sunnysign_oauth_state"); } catch { /* ignore */ }
      try { localStorage.removeItem("sunnysign_oauth_state"); } catch { /* ignore */ }
      clearAuthParamsFromUrl();
      return {
        success: true,
        access_token: token.access_token,
        expires_in: token.expires_in,
        wasPopupOAuth: popupMeta.wasPopupOAuth,
        oauthBridgeKey: popupMeta.bridgeKey,
        wasLaunchDialogOAuth,
      };
    }
  }

  // Implicit flow (Fragment): access_token im Hash (optional, falls verwendet)
  const hash = new URLSearchParams(window.location.hash.slice(1));
  const accessToken = hash.get("access_token");
  if (accessToken) {
    const expiresIn = hash.get("expires_in");
    const exp = expiresIn ? parseInt(expiresIn, 10) : undefined;
    setStoredToken(accessToken, exp);
    setConsentGiven();
    clearAuthParamsFromUrl();
    return { success: true, access_token: accessToken, expires_in: exp, wasLaunchDialogOAuth };
  }

  clearAuthParamsFromUrl();
  return { success: false, wasLaunchDialogOAuth };
}

function clearAuthParamsFromUrl(): void {
  if (typeof window === "undefined") return;
  try {
    const url = new URL(window.location.href);
    url.searchParams.delete("code");
    url.searchParams.delete("state");
    url.searchParams.delete("error");
    url.searchParams.delete("error_description");
    url.hash = "";
    // window.history.replaceState ist in Office-Dialog-Kontexten (displayDialogAsync) nicht verfügbar
    if (typeof window.history?.replaceState === "function") {
      window.history.replaceState({}, document.title, url.pathname + url.search);
    }
  } catch {
    /* In Office-Dialogen kann history.replaceState fehlen – ignorieren */
  }
}

interface TokenResponse {
  access_token: string;
  expires_in?: number;
}

/**
 * Tauscht den Authorization Code gegen ein Access Token (PKCE, kein client_secret).
 */
async function exchangeCodeForToken(
  code: string,
  codeVerifier: string,
  redirectUri: string = getRedirectUri()
): Promise<TokenResponse | null> {
  const body = new URLSearchParams({
    client_id: AUTH_CLIENT_ID,
    grant_type: "authorization_code",
    code,
    redirect_uri: redirectUri,
    code_verifier: codeVerifier,
  });

  const response = await fetch(`${AUTHORITY}/oauth2/v2.0/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: body.toString(),
  });

  if (!response.ok) {
    const text = await response.text();
    console.error("Token exchange failed:", response.status, text);
    return null;
  }

  const data = (await response.json()) as TokenResponse & { error?: string };
  if (data.error) {
    console.error("Token response error:", data.error);
    return null;
  }
  return { access_token: data.access_token, expires_in: data.expires_in };
}
